import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import styles from './Bird.module.css';

function BirdProfile() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [bird, setBird] = useState(null);
    const [birds, setBirds] = useState([]);
    const [aviaries, setAviaries] = useState([]);
    const [cages, setCages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');

    const [editFormData, setEditFormData] = useState({
        name: '',
        bird_band_right: '',
        bird_band_left: '',
        category: '',
        color: '',
        race: '',
        gender: 'undefined',
        birthday: '',
        aviaryId: '',
        birthing_cage: '',
        father: '',
        mother: '',
        status: 'Ativo',
        comments: ''
    });

    useEffect(() => {
        fetchBirdDetails();
    }, [id]);

    async function fetchBirdDetails() {
        setLoading(true);
        try {
            const token = JSON.parse(localStorage.getItem('token'));
            const headers = { Authorization: `Bearer ${token}` };

            const [birdRes, aviariesRes, cagesRes, allBirdsRes] = await Promise.all([
                api.get(`/bird/${id}`, { headers }),
                api.get('/aviary/myaviarys', { headers }).catch(() => ({ data: { aviary: [] } })),
                api.get('/cage/mycages', { headers }).catch(() => ({ data: { cages: [] } })),
                api.get('/bird/mybirds', { headers }).catch(() => ({ data: { birds: [] } }))
            ]);

            const fetchedBird = birdRes.data.bird;
            setBird(fetchedBird);
            setAviaries(aviariesRes.data.aviary || []);
            setCages(cagesRes.data.cages || []);
            setBirds(allBirdsRes.data.birds || []);

            setEditFormData({
                name: fetchedBird.name || '',
                bird_band_right: fetchedBird.bird_band_right || '',
                bird_band_left: fetchedBird.bird_band_left || '',
                category: fetchedBird.category || '',
                color: fetchedBird.color || '',
                race: fetchedBird.race || '',
                gender: fetchedBird.gender || 'undefined',
                comments: fetchedBird.comments || '',
                birthday: fetchedBird.birthday ? new Date(fetchedBird.birthday).toISOString().split('T')[0] : '',
                aviaryId: fetchedBird.aviary?._id || fetchedBird.aviaryId || '',
                birthing_cage: fetchedBird.birthing_cage?._id || fetchedBird.birthing_cage || '',
                current_cage: fetchedBird.current_cage?._id || fetchedBird.current_cage || '',
                father: fetchedBird.father?._id || fetchedBird.father || '',
                mother: fetchedBird.mother?._id || fetchedBird.mother || '',
                status: fetchedBird.status || 'Ativo'
            });
        } catch (error) {
            console.error('Erro ao carregar detalhes da ave:', error);
        } finally {
            setLoading(false);
        }
    }

    function handleEditChange(e) {
        const { name, value } = e.target;
        setEditFormData(prev => ({ ...prev, [name]: value }));
    }

    async function handleUpdateSubmit(e) {
        e.preventDefault();
        setErrorMessage('');

        try {
            const token = JSON.parse(localStorage.getItem('token'));

            const payload = {
                ...editFormData,
                birthday: editFormData.birthday ? new Date(editFormData.birthday).toISOString() : null,
                birthing_cage: editFormData.birthing_cage || null,
                father: editFormData.father || null,
                mother: editFormData.mother || null
            };

            const response = await api.put(`/bird/${id}`, payload, {
                headers: { Authorization: `Bearer ${token}` }
            });

            alert(response.data.message || 'Ave atualizada com sucesso!');
            setIsEditModalOpen(false);
            fetchBirdDetails();
        } catch (error) {
            setErrorMessage(error.response?.data?.message || 'Erro ao atualizar dados da ave.');
        }
    }

    // Função auxiliar para encontrar as informações da gaiola atual
    const getCurrentCageInfo = () => {
        if (!bird.current_cage) return 'Sem gaiola definida';
        
        // Se veio populado do backend como objeto
        if (typeof bird.current_cage === 'object') {
            return bird.current_cage.title || `Gaiola ${bird.current_cage.number}`;
        }

        // Se veio apenas como ObjectId (String), busca na lista de cages carregadas
        const foundCage = cages.find(c => c._id === bird.current_cage);
        if (foundCage) {
            return foundCage.title || `Gaiola ${foundCage.number}`;
        }

        return 'Gaiola não encontrada';
    };

    // Função auxilar para exibir o rótulo do sexo
    const getGenderLabel = (gender) => {
        if (gender === 'male') return 'Macho';
        if (gender === 'female') return 'Fêmea';
        return 'Indefinido';
    };

    // Função auxiliar para exibir o ícone do sexo
    const getGenderIcon = (gender) => {
        if (gender === 'male') return '♂';
        if (gender === 'female') return '♀';
        return '?';
    };

    // Função auxiliar para obter a classe CSS do ícone
    const getGenderClass = (gender) => {
        if (gender === 'male') return styles.male;
        if (gender === 'female') return styles.female;
        return styles.undefined || '';
    };

    if (loading) {
        return <p className={styles.loading}>Carregando perfil da ave...</p>;
    }

    if (!bird) {
        return (
            <div className={styles.container}>
                <p>Ave não encontrada.</p>
                <button className={styles.back_button} onClick={() => navigate('/bird')}>
                    ← Voltar para a lista
                </button>
            </div>
        );
    }

    const maleOptions = birds.filter(b => b.gender === 'male' && b._id !== bird._id);
    const femaleOptions = birds.filter(b => b.gender === 'female' && b._id !== bird._id);

    return (
        <div className={styles.container}>
            <div className={styles.profile_top_nav}>
                <button className={styles.back_button} onClick={() => navigate('/bird')}>
                    ← Voltar para a lista
                </button>
                <button className={styles.btn_edit} onClick={() => setIsEditModalOpen(true)}>
                    ✏️ Editar Ave
                </button>
            </div>

            <div className={styles.detail_header}>
                <div className={`${styles.gender_icon_large} ${getGenderClass(bird.gender)}`}>
                    {getGenderIcon(bird.gender)}
                </div>
                <div>
                    <h2>{bird.name}</h2>
                    <p className={styles.detail_subtitle}>
                        Anilha Direita: {bird.bird_band_right}
                    </p>
                    <p className={styles.detail_subtitle}>
                        Anilha Esquerda: {bird.bird_band_left}
                    </p>
                    <p className={styles.detail_subtitle}>
                        {getGenderLabel(bird.gender)}
                    </p>
                </div>
            </div>

            <div className={styles.section_box}>
                <h3>📖 Dados da ave</h3>
                <div className={styles.info_grid}>
                    <div className={styles.info_card}>
                        <span>NOME</span>
                        <strong>{bird.name}</strong>
                    </div>
                    <div className={styles.info_card}>
                        <span>SEXO</span>
                        <strong>{getGenderLabel(bird.gender)}</strong>
                    </div>
                    <div className={styles.info_card}>
                        <span>ANILHA ESQUERDA</span>
                        <strong>{bird.bird_band_left}</strong>
                    </div>
                    <div className={styles.info_card}>
                        <span>ANILHA DIREITA</span>
                        <strong>{bird.bird_band_right}</strong>
                    </div>
                    <div className={styles.info_card}>
                        <span>CATEGORIA</span>
                        <strong>{bird.category}</strong>
                    </div>
                    <div className={styles.info_card}>
                        <span>COR</span>
                        <strong>{bird.color}</strong>
                    </div>
                    <div className={styles.info_card}>
                        <span>RAÇA</span>
                        <strong>{bird.race}</strong>
                    </div>
                    <div className={styles.info_card}>
                        <span>NASCIMENTO</span>
                        <strong>{bird.birthday ? new Date(bird.birthday).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '-'}</strong>
                    </div>
                    <div className={styles.info_card}>
                        <span>GAIOLA DE NASCIMENTO</span>
                        <strong>{bird.birthing_cage ? (bird.birthing_cage.title || `Gaiola ${bird.birthing_cage.number}`) : '-'}</strong>
                    </div>
                    <div className={styles.info_card}>
                        <span>STATUS</span>
                        <strong>{bird.status || 'Ativo'}</strong>
                    </div>
                    <div className={styles.info_card}>
                        <span>COMENTÁRIO</span>
                        <strong>{bird.comments || ''}</strong>
                    </div>

                </div>
            </div>

            <div className={styles.section_box}>
                <h3>🏠 Localização atual</h3>
                <div className={styles.sub_card}>
                    <strong>Gaiola Atual: </strong>
                    <span>{getCurrentCageInfo()}</span>
                </div>
            </div>

            <div className={styles.section_box}>
                <h3>👨‍👩‍👧 Pais</h3>
                <div className={styles.list_group}>
                    <div className={styles.sub_card}>
                        {bird.father ? (
                            <p><strong>Pai:</strong> {bird.father.name} (Anilhass: {bird.father.bird_band_left} e {bird.father.bird_band_right})</p>
                        ) : (
                            <span className={styles.empty_text}>Pai não cadastrado.</span>
                        )}
                    </div><br></br>
                    <div className={styles.sub_card}>
                        {bird.mother ? (
                            <p><strong>Mãe:</strong> {bird.mother.name} (Anilhas: {bird.mother.bird_band_left} e {bird.father.bird_band_right})</p>
                        ) : (
                            <span className={styles.empty_text}>Mãe não cadastrada.</span>
                        )}
                    </div>
                </div>
            </div>

            <div className={styles.section_box}>
                <h3>👥 Irmãos</h3>
                <div className={styles.list_group}>
                    {bird.siblings && bird.siblings.length > 0 ? (
                        bird.siblings.map(sibling => (
                            <div key={sibling._id} className={styles.sub_card_row}>
                                <div className={styles.row_info}>
                                    <div className={`${styles.gender_icon_sm} ${getGenderClass(sibling.gender)}`}>
                                        {getGenderIcon(sibling.gender)}
                                    </div>
                                    <div>
                                        <strong>{sibling.name}</strong>
                                        <p>Anilhas:  ({sibling.bird_band_left} e  {sibling.bird_band_right}) • {sibling.color}</p>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className={styles.sub_card}>
                            <span className={styles.empty_text}>Nenhum irmão cadastrado.</span>
                        </div>
                    )}
                </div>
            </div>

            <div className={styles.section_box}>
                <h3>🐣 Filhos</h3>
                <div className={styles.list_group}>
                    {bird.children && bird.children.length > 0 ? (
                        bird.children.map(child => (
                            <div key={child._id} className={styles.sub_card_row}>
                                <div className={styles.row_info}>
                                    <div className={`${styles.gender_icon_sm} ${getGenderClass(child.gender)}`}>
                                        {getGenderIcon(child.gender)}
                                    </div>
                                    <div>
                                        <strong>{child.name}</strong>
                                        <p>Anilha: ({child.bird_band_left} e {child.bird_band_right}) • {child.color}</p>
                                    </div>
                                </div>
                            </div>
                        ))
                    ) : (
                        <div className={styles.sub_card}>
                            <span className={styles.empty_text}>Nenhum filho cadastrado.</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Modal de Edição */}
            {isEditModalOpen && (
                <div className={styles.modal_overlay}>
                    <div className={styles.modal_content}>
                        <div className={styles.modal_header}>
                            <h2>Editar Ave</h2>
                            <button className={styles.close_btn} onClick={() => setIsEditModalOpen(false)}>✕</button>
                        </div>
                        {errorMessage && <p className={styles.error_msg}>{errorMessage}</p>}
                        
                        <form onSubmit={handleUpdateSubmit} className={styles.form}>
                            <label>Aviário *</label>
                            <select name="aviaryId" value={editFormData.aviaryId} onChange={handleEditChange} required>
                                <option value="">Selecione um aviário</option>
                                {aviaries.map((av) => (
                                    <option key={av._id} value={av._id}>{av.name}</option>
                                ))}
                            </select>

                            <label>Nome</label>
                            <input type="text" name="name" value={editFormData.name} onChange={handleEditChange} />

                            <label>Anilha Esquerda</label>
                            <input type="text" name="bird_band_left" value={editFormData.bird_band_left} onChange={handleEditChange} />

                            <label>Anilha Diretira</label>
                            <input type="text" name="bird_band_right" value={editFormData.bird_band_right} onChange={handleEditChange} />
                            
                            <label>Categoria *</label>
                            <select name="category" value={editFormData.category} onChange={handleEditChange} required>
                                <option value="Canário De Canto">Canário De Canto</option>
                                <option value="Canário De Cor">Canário De Cor</option>
                                <option value="Canário De Porte">Canário De Porte</option>
                                <option value="Canário Sem Raça Definida (SRD)">Canário Sem Raça Definida (SRD)</option>
                                <option value="Agapornis">Agapornis</option>
                                <option value="Bicudo">Bicudo</option>
                                <option value="Calopsita">Calopsita</option>
                                <option value="Curió">Curió</option>
                                <option value="Mais Exóticos">Mais Exóticos</option>
                                <option value="Mais Psitacídeos">Mais Psitacídeos</option>
                                <option value="Periquito">Periquito</option>
                                <option value="Periquito Ondulado Australiano">Periquito Ondulado Australiano</option>
                                <option value="Pintassilgo">Pintassilgo</option>
                                <option value="Outros">Outros</option>
                            </select>

                            <label>Raça</label>
                            <input type="text" name="race" value={editFormData.race} onChange={handleEditChange} />

                            <label>Cor</label>
                            <input type="text" name="color" value={editFormData.color} onChange={handleEditChange} />

                            <label>Sexo *</label>
                            <select name="gender" value={editFormData.gender} onChange={handleEditChange} required>
                                <option value="undefined">Indefinido</option>
                                <option value="male">Macho</option>
                                <option value="female">Fêmea</option>
                            </select>

                            <label>Comentários</label>
                            <input type="text" name="comments" value={editFormData.comments} onChange={handleEditChange} />

                            <label>Gaiola Atual</label>
                            <select name="current_cage" value={editFormData.current_cage} onChange={handleEditChange}>
                                <option value="">Nenhuma / Não especificada</option>
                                {cages.map((cage) => (
                                    <option key={cage._id} value={cage._id}>
                                        {cage.title || `Gaiola ${cage.number}`}
                                    </option>
                                ))}
                            </select>

                            <label>Data de Nascimento *</label>
                            <input type="date" name="birthday" value={editFormData.birthday} onChange={handleEditChange} required />

                            <label>Gaiola de Nascimento</label>
                            <select name="birthing_cage" value={editFormData.birthing_cage} onChange={handleEditChange}>
                                <option value="">Nenhuma / Não especificada</option>
                                {cages.map((cage) => (
                                    <option key={cage._id} value={cage._id}>
                                        {cage.title || `Gaiola ${cage.number}`}
                                    </option>
                                ))}
                            </select>

                            <label>Pai</label>
                            <select name="father" value={editFormData.father} onChange={handleEditChange}>
                                <option value="">Selecione o Pai (Opcional)</option>
                                {maleOptions.map((b) => (
                                    <option key={b._id} value={b._id}>
                                        {b.name} (Anilhas: {b.bird_band_left} {b.bird_band_right})
                                    </option>
                                ))}
                            </select>

                            <label>Mãe</label>
                            <select name="mother" value={editFormData.mother} onChange={handleEditChange}>
                                <option value="">Selecione a Mãe (Opcional)</option>
                                {femaleOptions.map((b) => (
                                    <option key={b._id} value={b._id}>
                                        {b.name} (Anilhas: {b.bird_band_left} e {b.bird_band_right})
                                    </option>
                                ))}
                            </select>

                            <label>Status</label>
                            <select name="status" value={editFormData.status} onChange={handleEditChange}>
                                <option value="Ativo">Ativo</option>
                                <option value="Vendido">Vendido</option>
                                <option value="Falecido">Falecido</option>
                                <option value="Doado">Doado</option>
                            </select>

                            <div className={styles.modal_actions}>
                                <button type="button" onClick={() => setIsEditModalOpen(false)} className={styles.btn_cancel}>Cancelar</button>
                                <button type="submit" className={styles.btn_submit}>Salvar Alterações</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default BirdProfile;